import * as React from 'react'
import { styled, createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import MuiDrawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Container from '@mui/material/Container'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import PeopleIcon from '@mui/icons-material/People'
import ChromeReaderModeOutlinedIcon from '@mui/icons-material/ChromeReaderModeOutlined'
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined'
import MessageIcon from '@mui/icons-material/Message'
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined'
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import Groups3Icon from '@mui/icons-material/Groups3'
import { useSelector } from 'react-redux'
import { UserMenu } from './UserMenu'
import { State } from '../types/state'
import { useNavigate } from 'react-router'
import { ProfileContainer } from '../user/Profile'
import Paper from '@mui/material/Paper'
import { useState, MouseEvent } from 'react'
import Menu from '@mui/material/Menu'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import AdbIcon from '@mui/icons-material/Adb'
import { Role } from '../types/user'
import BarChartIcon from '@mui/icons-material/BarChart'

const drawerWidth: number = 240

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open === true && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    ...(open !== true && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9)
      }
    })
  }
}))

const defaultTheme = createTheme()

export function HeaderNormal ({ children }: any): JSX.Element {
  const { username } = useSelector((state: State) => state.user)
  const { isSpinnerVisible } = useSelector((state: State) => state.refresh)

  const [open, setOpen] = React.useState(true)
  const toggleDrawer = (): void => {
    setOpen(!open)
  }
  if (username === null) {
    return <>{children}</>
  }
  if (isSpinnerVisible) {
    return <></>
  }
  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position='absolute' open={open}>
          <Toolbar
            sx={{
              pr: '24px'
            }}
          >
            <IconButton
              edge='start'
              color='inherit'
              aria-label='open drawer'
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' })
              }}
            >
              <MenuIcon />
            </IconButton>
            <img src='./IB.png' style={{ width: '5%', marginRight: '1vw' }} />
            <Typography
              component='h1'
              variant='h6'
              color='inherit'
              noWrap
              sx={{ flexGrow: 1 }}
            >
              MyPath
            </Typography>
            {username !== null && <UserMenu username={username} />}
          </Toolbar>
        </AppBar>
        <Drawer variant='permanent' open={open}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1]
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component='nav'>
            <MainListItems />
          </List>
        </Drawer>
        <Box
          component='main'
          sx={{
            backgroundColor: theme =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto'
          }}
        >
          <Toolbar />
          <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
            <ProfileContainer component={Paper}>{children}</ProfileContainer>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export const MainListItems = (): JSX.Element => {
  const navigate = useNavigate()
  const user = useSelector((state: State) => state.user)

  const pathname = window.location.pathname

  const isAdminOrSuperadmin =
    user.role === Role.ADMIN || user.role === Role.SUPERADMIN

  const clickedStyle = {
    transform: 'scale(1.05)',
    backgroundColor: '#C8C8C8',
    color: '#080808'
  }

  return (
    <>
      {user.role === Role.STUDENT && (
        <>
          <ListItemButton
            onClick={() => navigate('subjects-selection')}
            style={pathname.includes('subjects-selection') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <HistoryEduOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary='Subjects Selection' />
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate('comments')}
            style={pathname.includes('comments') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <MessageIcon />
            </ListItemIcon>
            <ListItemText primary='Comments' />
          </ListItemButton>
        </>
      )}
      {isAdminOrSuperadmin && (
        <>
          <ListItemButton
            onClick={() => navigate('subjects')}
            style={pathname.includes('subjects') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <ChromeReaderModeOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary='Subjects' />
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate('commented')}
            style={pathname.includes('commented') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <MessageIcon />
            </ListItemIcon>
            <ListItemText primary='Comments' />
          </ListItemButton>
        </>
      )}
      <ListItemButton
        onClick={() => navigate('student-selections')}
        style={pathname.includes('student-selections') ? clickedStyle : {}}
      >
        <ListItemIcon>
          <FaceRetouchingNaturalOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary='Students Selections' />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate('subject-statistics')}
        style={pathname.includes('subject-statistics') ? clickedStyle : {}}
      >
        <ListItemIcon>
          <BarChartIcon />
        </ListItemIcon>
        <ListItemText primary='Subjects Statistics' />
      </ListItemButton>

      <ListItemButton
        onClick={() => navigate('subject-groups')}
        style={pathname.includes('subject-groups') ? clickedStyle : {}}
      >
        <ListItemIcon>
          <LibraryBooksOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary='Subject Groups' />
      </ListItemButton>
      {isAdminOrSuperadmin && (
        <>
          <ListItemButton
            onClick={() => navigate('users')}
            style={pathname.includes('users') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary='Users' />
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate('groups')}
            style={pathname === '/groups' ? clickedStyle : {}}
          >
            <ListItemIcon>
              <GroupsOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary='Groups' />
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate('parents')}
            style={pathname.includes('parents') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <FamilyRestroomOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary='Parents' />
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate('configuration')}
            style={pathname.includes('configuration') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <EngineeringOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary='Configuration' />
          </ListItemButton>
        </>
      )}
      {(user.role === Role.TEACHER || user.role === Role.PARENT) && (
        <>
          {user.role === Role.TEACHER && (
            <ListItemButton
              onClick={() => navigate('commented')}
              style={pathname.includes('commented') ? clickedStyle : {}}
            >
              <ListItemIcon>
                <MessageIcon />
              </ListItemIcon>
              <ListItemText primary='Comments' />
            </ListItemButton>
          )}
          <ListItemButton
            onClick={() => navigate('user-groups')}
            style={pathname.includes('user-groups') ? clickedStyle : {}}
          >
            <ListItemIcon>
              <Groups3Icon />
            </ListItemIcon>
            <ListItemText
              primary={user.role === Role.TEACHER ? 'My Groups' : 'My Children'}
            />
          </ListItemButton>
        </>
      )}
    </>
  )
}

const getUserPages = (role: Role) => {
  switch (role) {
    case Role.SUPERADMIN:
      return [
        'commented',
        'student-selections',
        'subject-statistics',
        'subjects',
        'subject-groups',
        'users',
        'groups',
        'parents',
        'configuration'
      ]
    case Role.ADMIN:
      return [
        'commented',
        'student-selections',
        'subject-statistics',
        'subjects',
        'subject-groups',
        'users',
        'groups',
        'parents',
        'configuration'
      ]
    case Role.TEACHER:
      return [
        'commented',
        'student-selections',
        'subject-statistics',
        'subject-groups',
        'user-groups'
      ]
    case Role.PARENT:
      return [
        'student-selections',
        'subject-statistics',
        'subject-groups',
        'user-groups'
      ]
    case Role.STUDENT:
      return [
        'student-selections',
        'comments',
        'subject-statistics',
        'subjects-selection',
        'subject-groups'
      ]
  }
}

export const HeaderMobile = ({ children }: any): JSX.Element => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const user = useSelector((state: State) => state.user)
  const { username } = user
  const { isSpinnerVisible } = useSelector((state: State) => state.refresh)

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>): void => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null)
  }

  const handleOpenPage = (option: string): void => {
    navigate(option)
  }

  if (username === null) {
    return <>{children}</>
  }
  if (isSpinnerVisible) {
    return <></>
  }

  return (
    <>
      <AppBar position='static'>
        <Container maxWidth='xl'>
          <CssBaseline />
          <Toolbar>
            <AdbIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
            <Typography
              variant='h6'
              component='div'
              onClick={() => handleOpenPage('/')}
              sx={{
                cursor: 'pointer',
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                color: 'inherit',
                textDecoration: 'none'
              }}
            >
              MyPath
            </Typography>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size='large'
                onClick={handleOpenNavMenu}
                color='inherit'
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id='menu-appbar'
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' }
                }}
              >
                {username !== null &&
                  getUserPages(user.role!).map(page => (
                    <MenuItem
                      key={page}
                      onClick={() => {
                        handleCloseNavMenu()
                        handleOpenPage(page)
                      }}
                    >
                      <Typography textAlign='center'>{page}</Typography>
                    </MenuItem>
                  ))}
                {username === null && (
                  <MenuItem
                    key='Login'
                    onClick={() => {
                      handleCloseNavMenu()
                      handleOpenPage('login')
                    }}
                  >
                    <Typography textAlign='center'>Login</Typography>
                  </MenuItem>
                )}
              </Menu>
            </Box>
            <Typography
              variant='h5'
              component='div'
              onClick={() => handleOpenPage('/')}
              sx={{
                cursor: 'pointer',
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                color: 'inherit',
                textDecoration: 'none'
              }}
            >
              MyPath
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex', marginLeft: '2vw' }
              }}
            >
              {username !== null &&
                getUserPages(user.role!).map(page => (
                  <Button
                    key={page}
                    onClick={() => handleOpenPage(page)}
                    style={{ color: 'white' }}
                    sx={{ my: 2, color: 'white', display: 'block' }}
                  >
                    {page}
                  </Button>
                ))}
              {username === null && (
                <Button
                  key='Login'
                  onClick={() => handleOpenPage('login')}
                  style={{ color: 'white' }}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  Login
                </Button>
              )}
            </Box>
            {username !== null && <UserMenu username={username} />}
          </Toolbar>
        </Container>
      </AppBar>
      {children}
    </>
  )
}
